/**
 * Color utility functions for generating color variations
 */

// Series palette shared by the charts (score pie, coach score trend).
export const DEFAULT_COLORS: string[] = [
  '#5fbfa5',
  '#2f80ed',
  '#f2994a',
  '#e5616d',
  '#a36bb2',
  '#f2c94c',
  '#56ccf2',
  '#6fcf97',
  '#bb6bd9',
  '#f78da7',
  '#ff6900',
  '#7bdcb5',
];

// Series color by index; wraps when the palette runs out.
export const getSeriesColor = (index: number, palette: string[] = DEFAULT_COLORS): string =>
  palette[index % palette.length];

export interface ColorVariations {
  primary: string;
  hoverBorder: string;
  hoverShadow: string;
  selectedBackground: string;
}

export class ColorUtils {
  // Constants for color variations
  private static readonly HOVER_BORDER_WHITE_FILTER = 0.5;
  private static readonly HOVER_SHADOW_WHITE_FILTER = 0.9;

  /**
   * Generates color variations for primary button states
   * @param primaryColor - The base primary color (hex format)
   * @returns Object containing all color variations
   */
  static generatePrimaryColorVariations(primaryColor: string): ColorVariations {
    return {
      primary: primaryColor,
      hoverBorder: this.applyWhiteFilter(primaryColor, this.HOVER_BORDER_WHITE_FILTER),
      hoverShadow: this.applyWhiteFilter(primaryColor, this.HOVER_SHADOW_WHITE_FILTER),
      selectedBackground: this.applyWhiteFilter(primaryColor, this.HOVER_SHADOW_WHITE_FILTER),
    };
  }

  /**
   * Applies a white filter to a color to create a lighter variation
   * @param color - The base color (hex format)
   * @param whiteAmount - Amount of white to mix (0-1, where 1 is pure white)
   * @returns The resulting color in hex format
   */
  static applyWhiteFilter(color: string, whiteAmount: number): string {
    try {
      // Clamp whiteAmount to valid range instead of throwing
      const clampedWhiteAmount = Math.max(0, Math.min(1, whiteAmount));

      // Remove # if present and validate hex format
      const hex = this.normalizeHexColor(color);

      // Parse RGB values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Mix with white
      const newR = Math.round(r + (255 - r) * clampedWhiteAmount);
      const newG = Math.round(g + (255 - g) * clampedWhiteAmount);
      const newB = Math.round(b + (255 - b) * clampedWhiteAmount);

      // Convert back to hex
      const toHex = (n: number) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('ColorUtils.applyWhiteFilter failed:', error);
      return color;
    }
  }

  /**
   * Normalizes a hex color string by removing # prefix and validating format
   * @param color - The color string to normalize
   * @returns Normalized hex color without # prefix
   */
  private static normalizeHexColor(color: string): string {
    const hex = color.replace('#', '').toLowerCase();

    if (!/^[0-9a-f]{6}$/.test(hex)) {
      throw new Error(`Invalid hex color format: ${color}`);
    }

    return hex;
  }

  /**
   * Converts RGB values to hex string
   * @param r - Red value (0-255)
   * @param g - Green value (0-255)
   * @param b - Blue value (0-255)
   * @returns Hex color string
   */
  static rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Converts hex color to RGB values
   * @param hex - Hex color string
   * @returns RGB values as object
   */
  static hexToRgb(hex: string): { r: number; g: number; b: number } {
    const normalized = this.normalizeHexColor(hex);
    return {
      r: parseInt(normalized.substring(0, 2), 16),
      g: parseInt(normalized.substring(2, 4), 16),
      b: parseInt(normalized.substring(4, 6), 16),
    };
  }

  /**
   * Returns a color with alpha/opacity removed (e.g. #RRGGBBAA → #RRGGBB, rgba → rgb).
   */
  static withoutOpacity(color: string | null | undefined): string {
    if (!color) {
      return '';
    }

    const trimmed = color.trim();

    // Handle hex colors (with or without alpha)
    if (trimmed.startsWith('#')) {
      if (trimmed.length === 9) {
        // #RRGGBBAA → #RRGGBB
        return trimmed.slice(0, 7);
      }
      if (trimmed.length === 5) {
        // #RGBA → #RGB
        return trimmed.slice(0, 4);
      }
      // Assume #RGB or #RRGGBB (no alpha)
      return trimmed;
    }

    // Handle rgba/argb/rgb (remove alpha if present)
    const rgbaMatch = trimmed.match(
      /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+%?)?\s*\)$/i,
    );
    if (rgbaMatch) {
      // Return as rgb() without alpha part
      return `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`;
    }

    // For anything else, return as is (do not handle other color models)
    return trimmed;
  }
}
